import type { ResultAsync } from 'neverthrow';
import { defineStore } from 'pinia';
import { computed, ref } from 'vue';
import { captureException } from '@sentry/vue';
import { useAuthStore } from './auth';
import { updateUserProfile } from '@/api/user';
import type { AuthUser } from '@/models/AuthUser';
import { getUserCountryCode } from '@/utilities/UserIPInfo';
import { FeatureFlag, isFeatureFlagEnabled } from '@/models/FeatureFlag';
import contentApiClient from '@/api/contentApiClient';
import { type UserSubscriptionInformationRaw, type UserSubscriptionRaw, hasActiveSubscription as hasAnyActiveSubscription } from '@/models/UserSubscriptionRaw';
import { type ExperienceLevel, experienceLevels } from '@/models/ExperienceLevel';

export const useUserStore = defineStore('user', () => {
	const qualifiesForTrial = ref(false);
	const billwerkCustomerId = ref<string | null>(null);
	const subscriptions = ref<UserSubscriptionRaw[]>([]);
	const experienceLevel = ref<ExperienceLevel>();

	const authStore = useAuthStore();

	const addCountryToUserProfile = (user: AuthUser, countryCode: string): ResultAsync<AuthUser, Error> => updateUserProfile(user.userId, { countryCode })
		.andThen(authStore.refreshAccessToken)
		.andThen(authStore.getCurrentUser);

	const maybeAddCountryToUserProfile = async (user: AuthUser): Promise<AuthUser> => {
		if (!isFeatureFlagEnabled(FeatureFlag.COUNTRY_CODE_PROFILE)) {
			return user;
		}

		if (user.country) {
			return user;
		}

		const countryCode = await getUserCountryCode();

		if (!countryCode) {
			return user;
		}

		const profileUpdateResult = await addCountryToUserProfile(user, countryCode);

		if (profileUpdateResult.isErr()) {
			captureException(profileUpdateResult.error);
			return user;
		}

		return profileUpdateResult.value;
	};

	const fetchSubscriptions = async () => {
		const response = await contentApiClient.client.get(`user/${authStore.user?.userId}/subscriptions`).json<UserSubscriptionInformationRaw>();

		qualifiesForTrial.value = response.qualifiesForTrial;
		billwerkCustomerId.value = response.billwerkCustomerId;
		subscriptions.value = response.subscriptions;
	};

	const hasActiveSubscription = computed(() => hasAnyActiveSubscription(subscriptions.value));

	const selectedExperienceLevel = computed(() => {
		if (!experienceLevel.value) {
			return undefined;
		}
		return experienceLevels[experienceLevel.value];
	});

	return {
		qualifiesForTrial,
		billwerkCustomerId,
		subscriptions,
		hasActiveSubscription,
		experienceLevel,
		selectedExperienceLevel,
		addCountryToUserProfile,
		maybeAddCountryToUserProfile,
		fetchSubscriptions,
	};
});
